import { useState } from "react";
import { IntegrationIcon, hasIntegrationIcon } from "@/components/IntegrationIcon";

// Every logo sits on the same white tile. The mark comes, in order, from a `logoUrl` the server sends, the
// dashboard's own registry of brand marks, the brand's own favicon, and only then a monogram, so a grid of apps
// reads as a row of real products rather than coloured boxes.

// Registry entries that are stand-ins (a mail glyph for Klaviyo, a wand for Higgsfield), not the brand's mark.
const STAND_INS = new Set(["klaviyo", "omnisend", "attentive", "intelligems", "honcho", "loop_subscriptions", "higgsfield", "google_merchant"]);

// The brand's own site, for its favicon. Apps missing here fall back to the monogram.
export const BRAND_DOMAINS = Object.freeze({
  klaviyo: "klaviyo.com", canva: "canva.com", monday: "monday.com", higgsfield: "higgsfield.ai", omnisend: "omnisend.com",
  attentive: "attentive.com", intelligems: "intelligems.io", loop: "loopwork.co", loop_subscriptions: "loopwork.co", gorgias: "gorgias.com",
  recharge: "rechargepayments.com", postscript: "postscript.io", amplitude: "amplitude.com", hex: "hex.tech", richpanel: "richpanel.com",
  picsio: "pics.io", trendtrack: "trendtrack.io", refero: "refero.design", parker: "parkerhq.com", honcho: "honcho.ai", google_merchant: "merchants.google.com",
  tiktok_shop: "tiktok.com", google_oauth: "google.com", brave_search: "brave.com", hackernews: "news.ycombinator.com",
});

export function faviconUrl(app) {
  // A review build that cannot reach the favicon host inlines the marks it needs through this map.
  const inline = typeof window !== "undefined" ? window.__growthosLogoOverrides?.[app] : null;
  if (inline) return inline;
  const domain = BRAND_DOMAINS[app];
  return domain ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128` : null;
}

const MONO_COLORS = ["#0F2442", "#003B8D", "#2F6DB5", "#0E7490", "#047857", "#7C3AED", "#BE185D", "#B45309", "#374151"];

export function monogramColor(seed = "") {
  let hash = 0;
  for (const char of String(seed)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return MONO_COLORS[hash % MONO_COLORS.length];
}

export function monogram(label = "", app = "") {
  const source = String(label || app).trim();
  return source ? source[0].toUpperCase() : "?";
}

/** Which source draws this app: "url", "icon", "favicon" or "monogram". */
export function logoSource(app, label, logoUrl) {
  if (logoUrl) return "url";
  if (!STAND_INS.has(app) && (hasIntegrationIcon({ id: app }) || hasIntegrationIcon({ name: label }))) return "icon";
  if (faviconUrl(app)) return "favicon";
  return "monogram";
}

export function ConnectionLogo({ app, label, logoUrl = null, size = 44, radius }) {
  const [failed, setFailed] = useState(false);
  const iconSize = Math.round(size * 0.5);
  const style = { width: size, height: size, borderRadius: radius ?? Math.round(size * 0.27) };
  const source = failed ? "monogram" : logoSource(app, label, logoUrl);
  if (source === "url" || source === "favicon") {
    const src = source === "url" ? logoUrl : faviconUrl(app);
    return <span className="cx-logo" style={style} aria-hidden="true" data-logo={source}><img src={src} alt="" width={Math.round(size * 0.6)} height={Math.round(size * 0.6)} loading="lazy" onError={() => setFailed(true)} /></span>;
  }
  if (source === "icon") {
    const icon = hasIntegrationIcon({ id: app }) ? <IntegrationIcon id={app} size={iconSize} /> : <IntegrationIcon name={label} size={iconSize} />;
    return <span className="cx-logo" style={style} aria-hidden="true" data-logo="icon">{icon}</span>;
  }
  const color = monogramColor(app || label);
  return (
    <span className="cx-logo cx-logo--mono" style={{ ...style, fontSize: Math.round(size * 0.42), background: `linear-gradient(145deg, color-mix(in srgb, ${color} 70%, #fff), ${color})` }} aria-hidden="true" data-logo="monogram" data-monogram={monogram(label, app)}>
      {monogram(label, app)}
    </span>
  );
}

export default ConnectionLogo;
